import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, X, Loader2, MessageCircle, Sparkles, ArrowRight,
  Users, Globe, BarChart3, ChevronRight, Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';

const hFont = { fontFamily: 'Manrope, sans-serif' };


/* ══════════════════════════════════════════════════
   1. SOCIAL PROOF STRIP
   ══════════════════════════════════════════════════ */
export function SocialProofStrip() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/public/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return null;

  const items = [
    { value: stats.transactions_classified?.toLocaleString() || '0', label: 'Transactions Classified', icon: BarChart3 },
    { value: stats.users?.toLocaleString() || '0', label: 'Users', icon: Users },
    { value: stats.countries?.toLocaleString() || '0', label: 'Countries', icon: Globe },
  ];

  return (
    <div className="bg-white border-y border-zinc-100 py-6" data-testid="social-proof-strip">
      <div className="max-w-5xl mx-auto px-6 flex justify-center gap-12 md:gap-20 flex-wrap">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-3 text-center">
            <item.icon className="w-5 h-5 text-[#D94E18] opacity-60" />
            <div>
              <p className="text-2xl font-bold text-zinc-900" style={hFont}>{item.value}</p>
              <p className="text-xs text-zinc-400 uppercase tracking-wider">{item.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════
   2. INTERACTIVE DEMO SECTION
   ══════════════════════════════════════════════════ */
const SAMPLE_DATA = `15/01/2025  Amazon Web Services  -149.99
16/01/2025  Starbucks Coffee     -6.50
17/01/2025  Client Payment ABC   +3,500.00
18/01/2025  Uber Technologies    -24.30
19/01/2025  Office Rent Transfer -2,100.00
20/01/2025  Adobe Creative Cloud -59.99
22/01/2025  Woolworths Food      -87.40`;

export function DemoSection() {
  const [txnText, setTxnText] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleClassify = async () => {
    const text = txnText.trim();
    if (!text) return;
    setLoading(true);
    setResults(null);
    setEmailSent(false);
    try {
      const res = await api.post('/public/demo-classify', { transactions: text });
      setResults(res.data);
    } catch (err) {
      setResults({ error: err.response?.data?.detail || 'Classification failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailResults = async () => {
    if (!email.trim()) return;
    try {
      await api.post('/gtm/leads', { email, source: 'demo', source_detail: 'demo_results' });
      setEmailSent(true);
    } catch { /* silent */ }
  };

  const categoryColor = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('revenue') || c.includes('income') || c.includes('sales')) return 'bg-emerald-100 text-emerald-700';
    if (c.includes('rent') || c.includes('lease')) return 'bg-violet-100 text-violet-700';
    if (c.includes('food') || c.includes('meal') || c.includes('grocery')) return 'bg-amber-100 text-amber-700';
    if (c.includes('transport') || c.includes('travel') || c.includes('uber')) return 'bg-blue-100 text-blue-700';
    if (c.includes('software') || c.includes('tech') || c.includes('cloud')) return 'bg-cyan-100 text-cyan-700';
    return 'bg-zinc-100 text-zinc-700';
  };

  const hasResults = results?.transactions && results.transactions.length > 0;

  return (
    <section className="py-20 bg-zinc-900" data-testid="demo-section" id="demo">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-5 bg-[#D94E18]/20 border border-[#D94E18]/30">
            <Sparkles className="w-3.5 h-3.5 text-[#D94E18]" />
            <span className="text-xs font-medium text-[#D94E18] tracking-wide uppercase">Try It Now</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={hFont}>
            See the AI in action
          </h2>
          <p className="text-white/70 mt-3 max-w-lg mx-auto">
            Paste a few transactions and watch Kaya classify them instantly. No signup, no email, no catch.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Input side */}
          <div className="space-y-3">
            <textarea
              value={txnText}
              onChange={e => setTxnText(e.target.value)}
              placeholder="Paste your transactions here...&#10;&#10;Example:&#10;2025-01-15, Uber Eats, -45.00&#10;2025-01-16, Shopify Revenue, 1200.00&#10;2025-01-17, AWS Cloud, -89.99"
              rows={8}
              className="w-full bg-white rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 text-sm font-mono focus:ring-2 focus:ring-[#D94E18] outline-none resize-none border-2 border-transparent"
              data-testid="demo-input"
            />
            <div className="flex items-center justify-between">
              <button
                onClick={() => setTxnText(SAMPLE_DATA)}
                className="text-xs text-[#D94E18] hover:underline font-semibold"
                data-testid="demo-sample-btn"
              >
                Use sample data
              </button>
              <p className="text-[10px] text-white/30">Sample size only — up to 20 transactions</p>
            </div>
            <Button
              onClick={handleClassify}
              disabled={loading || !txnText.trim()}
              className="w-full bg-[#D94E18] hover:bg-[#C2410C] text-white h-11 font-semibold rounded-lg"
              data-testid="demo-classify-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Classify my transactions <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </div>

          {/* Results side — shown like the real app */}
          <div className={`rounded-xl min-h-[300px] overflow-hidden ${hasResults ? 'bg-white' : 'bg-white/[0.08] border border-white/[0.15]'}`}>
            {!results && !loading && (
              <div className="flex items-center justify-center h-full text-white/50 text-sm p-5">
                <p>Results will appear here...</p>
              </div>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center h-full gap-3 p-5">
                <Loader2 className="w-8 h-8 animate-spin text-[#D94E18]" />
                <p className="text-white/60 text-sm">AI is classifying...</p>
              </div>
            )}
            {results?.error && (
              <div className="flex items-center justify-center h-full text-red-400 text-sm p-5">
                <p>{results.error}</p>
              </div>
            )}
            {hasResults && (
              <div className="h-full flex flex-col">
                {/* App-like header */}
                <div className="bg-stone-50 border-b border-stone-200 px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-stone-900" style={hFont}>Classified Transactions</p>
                    <p className="text-[11px] text-stone-500">{results.count} items processed</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-[10px] text-emerald-600 font-medium">AI Complete</span>
                  </div>
                </div>
                {/* Transaction list */}
                <div className="flex-1 overflow-y-auto max-h-[240px] divide-y divide-stone-100">
                  {results.transactions.map((tx) => (
                    <div key={tx.description} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-stone-50 transition-colors" data-testid={`demo-result-${tx.description}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-stone-900 font-medium truncate">{tx.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-[10px] ${categoryColor(tx.category)}`}>{tx.category}</Badge>
                          {tx.confidence && <span className="text-[10px] text-stone-400">{(tx.confidence * 100).toFixed(0)}%</span>}
                        </div>
                      </div>
                      <p className={`text-sm font-mono font-medium flex-shrink-0 ${tx.type === 'credit' || tx.amount > 0 ? 'text-emerald-600' : 'text-stone-900'}`}>
                        {typeof tx.amount === 'number' ? (tx.amount > 0 ? '+' : '') + tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : tx.amount}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Optional email — no gate, just an offer */}
                <div className="border-t border-stone-200 px-5 py-3 bg-stone-50">
                  {!emailSent ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="email"
                        placeholder="Email to receive results (optional)"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="h-8 text-xs bg-white border-stone-200"
                        data-testid="demo-email-input"
                      />
                      <Button onClick={handleEmailResults} disabled={!email.trim()} size="sm" variant="outline" className="h-8 text-xs flex-shrink-0 border-stone-200">
                        <Send className="w-3 h-3 mr-1" /> Send
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-emerald-600 text-center font-medium">Results sent to {email}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* After results — gentle nudge, not a gate */}
        {hasResults && (
          <div className="mt-8 text-center">
            <p className="text-white/50 text-sm mb-3">Like what you see? The full platform does this for entire bank statements.</p>
            <Link to="/login?register=true">
              <Button className="bg-[#D94E18] hover:bg-[#C2410C] text-white px-8 font-semibold" data-testid="demo-signup-btn">
                Get started free <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════
   3. HEALTH SCORE WIDGET
   ══════════════════════════════════════════════════ */


/* ══════════════════════════════════════════════════
   4. SAVINGS CALCULATOR
   ══════════════════════════════════════════════════ */
export function SavingsCalculator() {
  // Quantitative: task-specific hours
  const [bankStatements, setBankStatements] = useState(3);
  const [journalEntries, setJournalEntries] = useState(2);
  const [plPrep, setPlPrep] = useState(2);
  const [balanceSheet, setBalanceSheet] = useState(1);
  const [vatReturns, setVatReturns] = useState(2);
  const [rate, setRate] = useState(50);
  const [selectedPlan, setSelectedPlan] = useState(1); // index

  // Qualitative: value ratings (1-5 scale, each point = $200/mo perceived value)
  const [valPL, setValPL] = useState(3);
  const [valBS, setValBS] = useState(3);
  const [valVAT, setValVAT] = useState(3);
  const [valExpense, setValExpense] = useState(3);
  const [valReports, setValReports] = useState(3);

  const plans = [
    { name: 'Free', price: 0, label: '$0/mo' },
    { name: 'Starter', price: 9.99, label: '$9.99/mo' },
    { name: 'Pro', price: 29.99, label: '$29.99/mo' },
    { name: 'Business', price: 99.99, label: '$99.99/mo' },
    { name: 'Enterprise', price: 199.99, label: '$199.99/mo' },
  ];

  const [calcEmail, setCalcEmail] = useState('');
  const [calcEmailSent, setCalcEmailSent] = useState(false);

  const handleCalcEmail = async () => {
    if (!calcEmail.trim()) return;
    try {
      await api.post('/gtm/leads', { email: calcEmail, source: 'calculator', source_detail: `plan_${plans[selectedPlan].name}` });
      setCalcEmailSent(true);
    } catch { /* silent */ }
  };

  const totalHours = bankStatements + journalEntries + plPrep + balanceSheet + vatReturns;
  const currentAnnualCost = totalHours * rate * 12;
  const kayaAnnualCost = plans[selectedPlan].price * 12;
  const annualSavings = Math.max(0, currentAnnualCost - kayaAnnualCost);
  const savingsPercent = currentAnnualCost > 0 ? Math.round((annualSavings / currentAnnualCost) * 100) : 0;

  const valPerPoint = 200;
  const qualTotal = (valPL + valBS + valVAT + valExpense + valReports) * valPerPoint;

  const quantTasks = [
    { label: 'Processing bank statements', val: bankStatements, set: setBankStatements, id: 'bank-statements' },
    { label: 'Recording journal entries', val: journalEntries, set: setJournalEntries, id: 'journal-entries' },
    { label: 'Preparing Profit & Loss reports', val: plPrep, set: setPlPrep, id: 'pl-prep' },
    { label: 'Preparing Balance Sheets', val: balanceSheet, set: setBalanceSheet, id: 'balance-sheet' },
    { label: 'Preparing VAT / tax returns', val: vatReturns, set: setVatReturns, id: 'vat-returns' },
  ];

  const qualItems = [
    { label: 'Real-time Profit & Loss', val: valPL, set: setValPL, id: 'val-pl' },
    { label: 'Up-to-date Balance Sheet', val: valBS, set: setValBS, id: 'val-bs' },
    { label: 'Live VAT / tax position', val: valVAT, set: setValVAT, id: 'val-vat' },
    { label: 'Instant expense analysis', val: valExpense, set: setValExpense, id: 'val-expense' },
    { label: 'Always-ready financial reports', val: valReports, set: setValReports, id: 'val-reports' },
  ];

  const valLabels = ['Low', 'Some', 'Moderate', 'High', 'Critical'];

  return (
    <section className="py-20 bg-[#FAFAF9]" data-testid="savings-calculator" id="savings">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-5 bg-[#D94E18]/[0.06]">
            <Calculator className="w-3.5 h-3.5 text-[#D94E18]" />
            <span className="text-xs font-medium text-[#D94E18] tracking-wide uppercase">Your Time Back</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 tracking-tight" style={hFont}>
            What's your time really worth?
          </h2>
          <p className="text-base text-zinc-500 mt-3 max-w-2xl mx-auto">
            It's not just about saving money. It's about getting hours back in your week — time you could spend growing your business, not buried in spreadsheets.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* QUANTITATIVE: Hard Savings */}
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="quant-calculator">
            <div className="bg-zinc-900 px-6 py-4">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">The hard numbers</p>
              <h3 className="text-lg font-bold text-white mt-0.5" style={hFont}>Time & Money Savings</h3>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-zinc-500">How many hours per month do you spend on each?</p>
              {quantTasks.map(t => (
                <div key={t.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm text-zinc-700">{t.label}</label>
                    <span className="text-sm font-bold text-[#D94E18] tabular-nums w-8 text-right">{t.val}h</span>
                  </div>
                  <input
                    type="range" min="0" max="20" value={t.val}
                    onChange={e => t.set(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-[#D94E18]"
                    data-testid={`slider-${t.id}`}
                  />
                </div>
              ))}
              <div className="pt-3 border-t border-zinc-100">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-zinc-700">What do you pay per hour? <span className="text-zinc-400">(you or your bookkeeper)</span></label>
                  <span className="text-sm font-bold text-[#D94E18] tabular-nums">${rate}/hr</span>
                </div>
                <input
                  type="range" min="15" max="250" step="5" value={rate}
                  onChange={e => setRate(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-[#D94E18]"
                  data-testid="slider-rate"
                />
              </div>

              {/* Plan selector */}
              <div className="pt-3 border-t border-zinc-100">
                <p className="text-sm font-medium text-zinc-700 mb-3">Select the Kaya plan you'd use:</p>
                <div className="grid grid-cols-5 gap-1.5">
                  {plans.map((p, i) => (
                    <button
                      key={p.name}
                      onClick={() => setSelectedPlan(i)}
                      className={`py-2 px-1 rounded-lg text-center transition-all ${
                        selectedPlan === i
                          ? 'bg-[#D94E18] text-white ring-2 ring-[#D94E18] ring-offset-1'
                          : 'bg-zinc-50 text-zinc-600 hover:bg-zinc-100 border border-zinc-200'
                      }`}
                      data-testid={`plan-${p.name.toLowerCase()}`}
                    >
                      <p className={`text-[10px] font-bold ${selectedPlan === i ? 'text-white/70' : 'text-zinc-400'}`}>{p.name}</p>
                      <p className={`text-xs font-bold mt-0.5 ${selectedPlan === i ? 'text-white' : 'text-zinc-700'}`}>{p.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Results — hours first, then money */}
              <div className="bg-zinc-50 rounded-xl p-5 mt-2">
                <div className="text-center mb-4">
                  <p className="text-xs text-zinc-500 mb-1">Kaya gives you back</p>
                  <p className="text-4xl font-extrabold text-[#D94E18]" style={hFont}>{totalHours} hours<span className="text-lg font-medium text-zinc-400"> /month</span></p>
                  <p className="text-sm text-zinc-500 mt-1">That's <strong className="text-zinc-900">{(totalHours * 12).toLocaleString()} hours a year</strong> you're spending on manual bookkeeping</p>
                </div>
                <div className="h-px bg-zinc-200 my-3" />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-500">Your current annual cost</span>
                    <span className="text-base font-bold text-zinc-900 tabular-nums" style={hFont}>${currentAnnualCost.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-500">Kaya ({plans[selectedPlan].name} plan)</span>
                    <span className="text-base font-bold text-[#D94E18] tabular-nums" style={hFont}>${kayaAnnualCost.toLocaleString()}</span>
                  </div>
                  <div className="h-px bg-zinc-200" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-zinc-900">You save</span>
                    <div className="text-right">
                      <span className="text-xl font-extrabold text-emerald-600 tabular-nums" style={hFont} data-testid="quant-savings">${annualSavings.toLocaleString()}</span>
                      <span className="text-sm text-emerald-600 font-medium ml-1">/yr</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* QUALITATIVE: Value of Real-Time Data */}
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="qual-calculator">
            <div className="bg-[#D94E18] px-6 py-4">
              <p className="text-xs font-bold text-white/60 uppercase tracking-wider">The bigger picture</p>
              <h3 className="text-lg font-bold text-white mt-0.5" style={hFont}>Value of Real-Time Intelligence</h3>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-zinc-500">How valuable would it be to have each of these — instantly, at any time?</p>
              {qualItems.map(q => (
                <div key={q.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm text-zinc-700">{q.label}</label>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      q.val <= 1 ? 'bg-zinc-100 text-zinc-500' :
                      q.val <= 2 ? 'bg-blue-50 text-blue-600' :
                      q.val <= 3 ? 'bg-amber-50 text-amber-600' :
                      q.val <= 4 ? 'bg-orange-50 text-orange-600' :
                      'bg-red-50 text-red-600'
                    }`}>{valLabels[q.val - 1]}</span>
                  </div>
                  <input
                    type="range" min="1" max="5" value={q.val}
                    onChange={e => q.set(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-[#D94E18]"
                    data-testid={`slider-${q.id}`}
                  />
                </div>
              ))}

              {/* Results */}
              <div className="bg-[#D94E18]/[0.04] rounded-xl p-5 mt-2">
                <p className="text-xs text-zinc-500 text-center mb-2">Estimated monthly value of real-time financial data</p>
                <p className="text-4xl font-extrabold text-[#D94E18] text-center" style={hFont} data-testid="qual-total">
                  ${qualTotal.toLocaleString()}<span className="text-base font-medium text-zinc-400">/mo</span>
                </p>
                <p className="text-xs text-zinc-400 text-center mt-2">Based on industry benchmarks for decision-making speed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Combined bottom line — time first, then money */}
        <div className="mt-8 bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8 text-center" data-testid="combined-value">
          <p className="text-sm text-zinc-500 mb-4">Every year without Kaya, you're losing</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
            <div>
              <p className="text-4xl sm:text-5xl font-extrabold text-[#D94E18]" style={hFont}>{(totalHours * 12).toLocaleString()}h</p>
              <p className="text-sm text-zinc-500 mt-1">of your time</p>
            </div>
            <div className="hidden sm:block text-3xl text-zinc-300 font-light">+</div>
            <div className="sm:hidden text-lg text-zinc-300 font-light">+</div>
            <div>
              <p className="text-4xl sm:text-5xl font-extrabold text-zinc-900" style={hFont}>${annualSavings.toLocaleString()}</p>
              <p className="text-sm text-zinc-500 mt-1">in bookkeeping costs</p>
            </div>
            <div className="hidden sm:block text-3xl text-zinc-300 font-light">+</div>
            <div className="sm:hidden text-lg text-zinc-300 font-light">+</div>
            <div>
              <p className="text-4xl sm:text-5xl font-extrabold text-emerald-600" style={hFont}>${(qualTotal * 12).toLocaleString()}</p>
              <p className="text-sm text-zinc-500 mt-1">in real-time value</p>
            </div>
          </div>
          <div className="mt-6">
            <Link to="/login?register=true">
              <Button size="lg" className="bg-[#D94E18] hover:bg-[#C2410C] text-white text-base px-8" data-testid="savings-signup-btn">
                Get your time back — start free <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          {/* Soft email capture — not a gate */}
          <div className="mt-5 pt-5 border-t border-zinc-100">
            {!calcEmailSent ? (
              <div className="flex flex-col sm:flex-row items-center gap-2 max-w-md mx-auto">
                <p className="text-xs text-zinc-400 sm:hidden mb-1">Want us to send you this breakdown?</p>
                <Input
                  type="email" placeholder="Your email — we'll send you this report"
                  value={calcEmail} onChange={e => setCalcEmail(e.target.value)}
                  className="h-9 text-xs bg-zinc-50 border-zinc-200"
                  data-testid="calc-email-input"
                />
                <Button onClick={handleCalcEmail} disabled={!calcEmail.trim()} size="sm" variant="outline" className="h-9 text-xs flex-shrink-0 border-zinc-200">
                  <Send className="w-3 h-3 mr-1" /> Send report
                </Button>
              </div>
            ) : (
              <p className="text-xs text-emerald-600 text-center font-medium" data-testid="calc-email-success">We'll send your personalised report to {calcEmail}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


/* ══════════════════════════════════════════════════
   5. EXIT-INTENT POPUP
   ══════════════════════════════════════════════════ */


/* ══════════════════════════════════════════════════
   6. AI CHAT WIDGET (Floating)
   ══════════════════════════════════════════════════ */
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm Kaya. Got questions about AI-powered accounting? Ask me anything." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `pub-${Date.now()}`);
  const scrollRef = useRef(null);

  const quickQuestions = [
    "What file formats do you support?",
    "How does the AI classify transactions?",
    "What's included in the free plan?",
  ];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const res = await api.post('/public/chat', { message: text, session_id: sessionId, history });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble right now. Try again in a moment!" }]);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, sessionId]);

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#D94E18] text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
          data-testid="chat-widget-bubble"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden" style={{ height: '480px' }} data-testid="chat-widget-window">
          {/* Header */}
          <div className="bg-zinc-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#D94E18] rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Kaya</p>
                <p className="text-[10px] text-zinc-400">AI Product Assistant</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="chat-widget-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
            {messages.map((msg, i) => (
              <div key={`msg-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#D94E18] text-white rounded-br-sm'
                    : 'bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-200 rounded-xl px-4 py-3 rounded-bl-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-zinc-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-zinc-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-zinc-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick questions (only if no user messages yet) */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 bg-white border-t border-zinc-100 flex gap-1.5 flex-wrap">
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="text-[11px] px-2.5 py-1.5 rounded-full border border-zinc-200 text-zinc-600 hover:border-[#D94E18] hover:text-[#D94E18] transition-colors"
                  data-testid={`chat-quick-q-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 bg-white border-t border-zinc-200 flex-shrink-0">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
                placeholder="Ask Kaya anything..."
                className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:border-[#D94E18] focus:ring-1 focus:ring-[#D94E18] outline-none"
                disabled={loading}
                data-testid="chat-widget-input"
              />
              <Button
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                size="sm"
                className="bg-[#D94E18] hover:bg-[#C2410C] text-white"
                data-testid="chat-widget-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
